import React, { useState, useEffect, useRef } from 'react';
import { Search, Sparkles, Clock, TrendingUp, X, ArrowRight, Filter, Zap, Pin, FileText, Package, HelpCircle, FolderOpen, Newspaper, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePremiumSearch } from '@/hooks/usePremiumSearch';
import type { SearchItem } from '@/utils/searchIndex';

type SuggestionItem = { label: string; item?: SearchItem };
import { useSimpleAISearch } from '@/hooks/useSimpleAISearch';
import { applyHighlights, generateSnippet } from '@/utils/hybridSearch';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContentTypeIcon: React.FC<{ type: string; className?: string }> = ({ type, className }) => {
  const iconClass = cn("h-4 w-4", className);
  switch (type) {
    case 'product': return <Package className={cn(iconClass, "text-blue-400")} />;
    case 'page': return <FileText className={cn(iconClass, "text-slate-400")} />;
    case 'feature': return <Zap className={cn(iconClass, "text-amber-400")} />;
    case 'category': return <FolderOpen className={cn(iconClass, "text-purple-400")} />;
    case 'blog': return <Newspaper className={cn(iconClass, "text-emerald-400")} />;
    case 'faq': return <HelpCircle className={cn(iconClass, "text-cyan-400")} />;
    case 'case-study': return <BarChart3 className={cn(iconClass, "text-rose-400")} />;
    default: return <FileText className={cn(iconClass, "text-slate-300")} />;
  }
};

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const {
    query, setQuery, selectedCategory, setSelectedCategory,
    isSearching, searchResults, suggestions, recentSearches,
    popularSearches, searchCategories, handleSearch, clearSearch,
    trackResultClick, totalIndexedItems, hasSynonyms
  } = usePremiumSearch();

  const { aiResponse, isLoading: isAILoading, searchWithAI, clearResponse } = useSimpleAISearch();

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      switch (e.key) {
        case 'Escape': onClose(); break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => prev < (showSuggestions ? suggestions.length - 1 : searchResults.length - 1) ? prev + 1 : prev);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0) {
            if (showSuggestions && suggestions[selectedIndex]) {
              handleSuggestionClick(suggestions[selectedIndex]);
            } else if (searchResults[selectedIndex]) {
              handleResultClick(searchResults[selectedIndex], selectedIndex);
            }
          } else if (query.trim()) {
            setShowSuggestions(false);
            handleSearch(query);
            if (query.length > 3) searchWithAI(query);
          }
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, showSuggestions, suggestions, searchResults, query, handleSearch, searchWithAI, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(value.length >= 2);
    setSelectedIndex(-1);
  };

  const handleSuggestionClick = (suggestion: SuggestionItem) => {
    setShowSuggestions(false);
    setSelectedIndex(-1);
    // If the suggestion is bound to a concrete index item, navigate directly —
    // this guarantees the click result matches the suggestion label.
    if (suggestion.item) {
      setQuery(suggestion.label);
      window.location.href = suggestion.item.url;
      onClose();
      return;
    }
    setQuery(suggestion.label);
    handleSearch(suggestion.label);
    if (suggestion.label.length > 3) searchWithAI(suggestion.label);
  };

  const handleResultClick = (result: typeof searchResults[0], index: number) => {
    trackResultClick(result, index);
    window.location.href = result.url;
    onClose();
  };

  const handleClose = () => {
    clearSearch();
    clearResponse();
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onClose();
  };

  const renderHighlightedText = (text: string, queryStr: string) => {
    const lowerText = text.toLowerCase();
    const lowerQuery = queryStr.toLowerCase().trim();
    if (!lowerQuery) return text;
    const highlights: React.ReactNode[] = [];
    let lastIndex = 0;
    let index = lowerText.indexOf(lowerQuery);
    while (index !== -1) {
      if (index > lastIndex) highlights.push(<span key={`t-${lastIndex}`}>{text.slice(lastIndex, index)}</span>);
      highlights.push(
        <mark key={`m-${index}`} className="bg-blue-500/20 text-blue-300 font-medium rounded px-0.5">{text.slice(index, index + lowerQuery.length)}</mark>
      );
      lastIndex = index + lowerQuery.length;
      index = lowerText.indexOf(lowerQuery, lastIndex);
    }
    if (lastIndex < text.length) highlights.push(<span key="end">{text.slice(lastIndex)}</span>);
    return highlights.length > 0 ? <>{highlights}</> : text;
  };

  if (!isOpen) return null;

  const hasQuery = query.trim().length > 0;
  const showEmptyState = !hasQuery && !aiResponse;
  const showResults = hasQuery && searchResults.length > 0;
  const showNoResults = hasQuery && searchResults.length === 0 && !isSearching;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[100]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-modal-title"
    >
      {/* Layered backdrop: blur + dark overlay + subtle noise */}
      <div className="absolute inset-0 backdrop-blur-2xl bg-black/40" onClick={handleClose} />
      
      {/* Ambient glow orbs behind modal */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[10%] left-[20%] w-[500px] h-[500px] rounded-full bg-blue-600/8 blur-[120px]" />
        <div className="absolute top-[20%] right-[15%] w-[400px] h-[400px] rounded-full bg-purple-600/8 blur-[100px]" />
        <div className="absolute bottom-[10%] left-[40%] w-[300px] h-[300px] rounded-full bg-cyan-500/6 blur-[80px]" />
      </div>

      <div className="relative min-h-screen flex items-start justify-center pt-0 sm:pt-[5vh] md:pt-[8vh] px-0 sm:px-4">
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 24 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 24 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          className={cn(
            "relative w-full overflow-hidden isolate",
            "h-screen sm:h-auto",
            "rounded-none sm:rounded-2xl md:rounded-3xl",
            "max-w-full sm:max-w-2xl md:max-w-4xl lg:max-w-6xl",
            // Apple-style glassmorphism: translucent + heavy blur + saturation
            "bg-gradient-to-b from-slate-900/70 via-slate-950/75 to-slate-900/70",
            "backdrop-blur-2xl backdrop-saturate-150",
            "border border-white/[0.12]",
            "shadow-[0_8px_32px_rgba(0,0,0,0.45),_0_0_0_1px_rgba(255,255,255,0.06)_inset,_0_32px_64px_-12px_rgba(0,0,0,0.55)]",
            "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.10),transparent_60%)]"
          )}
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <h2 id="search-modal-title" className="sr-only">Search Dialgia</h2>
          <p id="search-hint" className="sr-only">Type to search products, features, or ask AI for help. Press Escape to close.</p>

          {/* ─── Search Header ─── */}
          <div className="flex items-center px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 border-b border-white/[0.08]">
            <div className="relative mr-4">
              <Search className="h-5 w-5 text-white/50" />
              {hasSynonyms && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-400 rounded-full ring-2 ring-white/10" title="Semantic search active" />
              )}
            </div>
            <div className="flex-1 relative">
              <label htmlFor="global-search" className="sr-only">Search products, features, or ask AI</label>
              <input
                ref={inputRef}
                id="global-search"
                type="search"
                value={query}
                onChange={handleInputChange}
                placeholder="Search products, features, or ask AI anything..."
                className="w-full text-lg sm:text-xl font-light tracking-wide bg-transparent text-white placeholder:text-white/80 caret-blue-400 border-0 outline-none ring-0 shadow-none appearance-none focus:outline-none focus:ring-0 focus:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:border-0 [&::-webkit-search-cancel-button]:appearance-none"
                aria-label="Search products, features, or ask AI"
                aria-describedby="search-hint"
                autoComplete="off"
              />

              {/* Smart Suggestions dropdown removed per design — results list is the single source of truth. */}

            </div>

            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-1.5">
                <kbd className="px-2 py-1 bg-white/[0.06] border border-white/[0.1] rounded-md text-[11px] text-white/80 font-medium">⌘</kbd>
                <kbd className="px-2 py-1 bg-white/[0.06] border border-white/[0.1] rounded-md text-[11px] text-white/80 font-medium">K</kbd>
              </div>
              <button
                onClick={handleClose}
                className="h-8 w-8 rounded-full flex items-center justify-center bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.08] transition-all text-white/80 hover:text-white/70"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* ─── Category Filter Bar ─── */}
          {hasQuery && (
            <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 py-3 border-b border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 -mb-1 scrollbar-none">
                {searchCategories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 min-h-[36px] rounded-full text-sm whitespace-nowrap transition-all",
                      selectedCategory === category.id
                        ? "bg-blue-500/20 text-blue-300 border border-blue-400/20 shadow-[0_0_12px_rgba(59,130,246,0.15)]"
                        : "text-white/80 hover:text-white/60 hover:bg-white/[0.04] border border-transparent"
                    )}
                  >
                    <span>{category.icon}</span>
                    <span className="hidden sm:inline">{category.label}</span>
                    {category.count > 0 && (
                      <span className={cn(
                        "text-[10px] font-medium px-1.5 py-0.5 rounded-full",
                        selectedCategory === category.id
                          ? "bg-blue-400/20 text-blue-300"
                          : "bg-white/[0.06] text-white/80"
                      )}>
                        {category.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className="hidden md:flex items-center gap-3 text-xs text-white/25 flex-shrink-0 ml-4">
                {hasSynonyms && (
                  <span className="flex items-center gap-1.5 text-emerald-400/70">
                    <Sparkles className="h-3 w-3" />
                    Semantic
                  </span>
                )}
                <span>{totalIndexedItems.toLocaleString()} indexed</span>
              </div>
            </div>
          )}

          {/* ─── Content Area ─── */}
          <div className="max-h-[calc(100vh-180px)] sm:max-h-[60vh] overflow-y-auto overscroll-contain scrollbar-none" style={{ WebkitOverflowScrolling: 'touch' }}>

            {/* Empty State */}
            {showEmptyState && (
              <div className="px-4 sm:px-6 md:px-8 py-10 sm:py-14">
                <div className="text-center mb-10">
                  {/* Animated icon with glow */}
                  <div className="relative inline-flex mb-6">
                    <div className="absolute inset-0 rounded-full bg-blue-500/20 blur-xl animate-pulse" />
                    <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/[0.1] flex items-center justify-center">
                      <Sparkles className="h-8 w-8 text-blue-400" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-white/90 mb-2 tracking-tight">Discover Dialgia Solutions</h3>
                  <p className="text-white/80 text-sm max-w-md mx-auto">
                    Search across products, features, and ask our AI assistant
                  </p>
                </div>

                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <div className="mb-8">
                    <h4 className="flex items-center text-xs font-medium text-white/80 uppercase tracking-widest mb-3">
                      <Clock className="h-3.5 w-3.5 mr-2" />
                      Recent Searches
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map(search => (
                        <button
                          key={search}
                          onClick={() => handleSuggestionClick({ label: search })}
                          className="px-3.5 py-1.5 text-xs text-white/50 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] hover:border-white/[0.12] rounded-full transition-all hover:text-white/70"
                        >
                          {search}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular Searches */}
                <div>
                  <h4 className="flex items-center text-xs font-medium text-white/80 uppercase tracking-widest mb-3">
                    <TrendingUp className="h-3.5 w-3.5 mr-2" />
                    Popular Searches
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {popularSearches.map(search => (
                      <button
                        key={search}
                        onClick={() => handleSuggestionClick({ label: search })}
                        className="group px-4 py-2 text-sm text-white/50 bg-white/[0.03] hover:bg-white/[0.07] border border-white/[0.06] hover:border-white/[0.14] rounded-xl transition-all hover:text-white/80 hover:shadow-[0_0_20px_rgba(59,130,246,0.06)]"
                      >
                        {search}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Search Results */}
            {showResults && (
              <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6">
                <div className="space-y-2">
                  {searchResults.map((result, index) => (
                    <motion.div
                      key={result.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.025, duration: 0.2 }}
                      onClick={() => handleResultClick(result, index)}
                      className={cn(
                        "group cursor-pointer p-4 rounded-2xl border transition-all",
                        "bg-white/[0.02] hover:bg-white/[0.06]",
                        "border-white/[0.04] hover:border-white/[0.12]",
                        "hover:shadow-[0_4px_24px_rgba(0,0,0,0.2)]",
                        selectedIndex === index && "bg-white/[0.06] border-blue-400/20",
                        result.isPinned && "bg-gradient-to-r from-amber-500/[0.04] to-transparent border-amber-400/10"
                      )}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            {result.isPinned && <Pin className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />}
                            <ContentTypeIcon type={result.type} />
                            <h3 className="font-medium text-base text-white/80 group-hover:text-white transition-colors truncate">
                              {renderHighlightedText(result.title, query)}
                            </h3>
                            <span className={cn(
                              "text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0",
                              result.isPinned
                                ? "bg-amber-400/15 text-amber-300/80"
                                : "bg-white/[0.06] text-white/80"
                            )}>
                              {result.type}
                            </span>
                            {result.matchType === 'semantic' && (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-400/10 text-blue-300/70 flex items-center gap-1 flex-shrink-0">
                                <Sparkles className="h-2.5 w-2.5" />
                                Related
                              </span>
                            )}
                            {result.matchType === 'fuzzy' && (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-300/70 flex-shrink-0">
                                Similar
                              </span>
                            )}
                          </div>
                          <p className="text-white/35 mb-2 line-clamp-2 text-sm leading-relaxed">
                            {renderHighlightedText(generateSnippet(result.description, query, 180), query)}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] text-white/25 bg-white/[0.04] px-2.5 py-0.5 rounded-full">
                              {result.category}
                            </span>
                            {result.url.includes('#') && (
                              <span className="text-[10px] text-blue-400/50 flex items-center gap-1">
                                <ArrowRight className="h-2.5 w-2.5" />
                                Direct link
                              </span>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-white/80 group-hover:text-white/80 transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {showNoResults && (
              <div className="px-8 py-14 text-center">
                <div className="relative inline-flex mb-5">
                  <div className="absolute inset-0 rounded-full bg-white/5 blur-xl" />
                  <Search className="relative h-12 w-12 text-white/80" />
                </div>
                <h3 className="text-lg font-medium text-white/70 mb-2">No results found</h3>
                <p className="text-white/80 mb-6 text-sm">
                  Try different keywords or ask our AI assistant
                </p>
                <button
                  onClick={() => searchWithAI(query)}
                  disabled={isAILoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-blue-500/15 text-blue-300 border border-blue-400/20 hover:bg-blue-500/25 hover:border-blue-400/30 transition-all shadow-[0_0_20px_rgba(59,130,246,0.1)] disabled:opacity-50"
                >
                  <Zap className="h-4 w-4" />
                  <span>{isAILoading ? 'Asking AI...' : 'Ask AI Assistant'}</span>
                </button>
              </div>
            )}

            {/* AI Response */}
            {aiResponse && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="mx-4 sm:mx-6 md:mx-8 my-4 p-5 rounded-2xl bg-gradient-to-br from-blue-500/[0.06] to-purple-500/[0.06] border border-blue-400/[0.08]"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/30 to-purple-600/30 border border-white/[0.1] flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.15)]">
                      <Sparkles className="h-5 w-5 text-blue-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-white/80 mb-2 text-sm">AI Assistant</h4>
                    <p className="text-white/50 text-sm leading-relaxed mb-4">{aiResponse.response}</p>
                    {aiResponse.followUpQuestions && aiResponse.followUpQuestions.length > 0 && (
                      <div>
                        <h5 className="text-[10px] font-medium text-white/25 uppercase tracking-widest mb-2">Follow-up</h5>
                        <div className="space-y-1">
                          {aiResponse.followUpQuestions.map((question, index) => (
                            <button
                              key={index}
                              onClick={() => handleSuggestionClick({ label: question })}
                              className="block text-sm text-blue-400/70 hover:text-blue-300 transition-colors"
                            >
                              → {question}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Loading */}
            {(isSearching || isAILoading) && (
              <div className="px-8 py-14 text-center">
                <div className="relative inline-flex mb-4">
                  <div className="absolute inset-0 rounded-full bg-blue-500/10 blur-xl animate-pulse" />
                  <div className="relative w-10 h-10 border-2 border-white/10 border-t-blue-400/60 rounded-full animate-spin" />
                </div>
                <p className="text-white/80 text-sm">
                  {isAILoading ? 'AI is thinking...' : 'Searching...'}
                </p>
              </div>
            )}
          </div>

          {/* ─── Footer hint ─── */}
          <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 py-3 border-t border-white/[0.04] text-[11px] text-white/15">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-white/[0.04] rounded text-[10px]">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-white/[0.04] rounded text-[10px]">↵</kbd>
                select
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-white/[0.04] rounded text-[10px]">esc</kbd>
                close
              </span>
            </div>
            <span className="hidden sm:block">Powered by Dialgia AI</span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};
